import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Star, 
  Heart,
  Play,
  Film,
  Tv,
  Users,
  Award
} from 'lucide-react';
import { DetailedMovie, DetailedTVSeries, Credits, Video } from '../types';
import { 
  getMovieDetails, 
  getTVSeriesDetails, 
  getMovieCredits, 
  getTVSeriesCredits,
  getImageUrl
} from '../api/tmdb';
import { useFavorites } from '../hooks/useFavorites';
import TrailerModal from '../components/TrailerModal';

const Details: React.FC = () => {
  const { type, id } = useParams<{ type: 'movie' | 'tv'; id: string }>();
  const [details, setDetails] = useState<DetailedMovie | DetailedTVSeries | null>(null);
  const [credits, setCredits] = useState<Credits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isTrailerOpen, setIsTrailerOpen] = useState(false);
  const [selectedTrailer, setSelectedTrailer] = useState<Video | null>(null);
  const { addToFavorites, removeFromFavorites, isFavorite } = useFavorites();

  const loadDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const itemId = parseInt(id!);
      
      // Load details and credits in parallel
      const [detailsData, creditsData] = await Promise.all([
        type === 'movie' ? getMovieDetails(itemId) : getTVSeriesDetails(itemId),
        type === 'movie' ? getMovieCredits(itemId) : getTVSeriesCredits(itemId)
      ]);

      setDetails(detailsData);
      setCredits(creditsData);
    } catch (err) {
      console.error('Error loading details:', err);
      setError('Failed to load details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [type, id]);

  useEffect(() => {
    if (type && id) {
      loadDetails();
    }
  }, [type, id, loadDetails]);

  const handleFavoriteToggle = () => {
    if (!details) return;

    const title = 'title' in details ? details.title : details.name;
    const favoriteItem = {
      id: details.id,
      type: type!,
      title: title || 'Unknown Title',
      poster_path: details.poster_path,
      vote_average: details.vote_average,
      addedAt: Date.now(),
    };

    if (isFavorite(details.id, type!)) {
      removeFromFavorites(details.id, type!);
    } else {
      addToFavorites(favoriteItem);
    }
  };

  const handleWatchTrailer = () => {
    if (!details?.videos?.results) return;
    
    // Find the official trailer or first trailer
    const trailer = details.videos.results.find(
      video => video.type === 'Trailer' && video.site === 'YouTube'
    ) || details.videos.results.find(
      video => video.site === 'YouTube'
    );
    
    if (trailer) {
      setSelectedTrailer(trailer);
      setIsTrailerOpen(true);
    }
  };

  const hasTrailer = details?.videos?.results?.some(
    video => video.type === 'Trailer' && video.site === 'YouTube'
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-300 dark:bg-dark-700 rounded w-32"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="h-96 bg-gray-300 dark:bg-dark-700 rounded-xl"></div>
            </div>
            <div className="space-y-4">
              <div className="h-8 bg-gray-300 dark:bg-dark-700 rounded w-3/4"></div>
              <div className="h-4 bg-gray-300 dark:bg-dark-700 rounded w-1/2"></div>
              <div className="h-4 bg-gray-300 dark:bg-dark-700 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !details) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-4">{error || 'Item not found'}</div>
          <Link
            to="/"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Go Home</span>
          </Link>
        </div>
      </div>
    );
  }

  const title = 'title' in details ? details.title : details.name;
  const releaseDate = 'release_date' in details ? details.release_date : details.first_air_date;
  const runtime = 'runtime' in details ? details.runtime : details.episode_run_time[0];
  const status = details.status;
  const genres = details.genres;
  const overview = details.overview;
  const tagline = details.tagline;
  const voteAverage = typeof details.vote_average === 'number' && !isNaN(details.vote_average) 
    ? details.vote_average.toFixed(1) 
    : 'N/A';
  const voteCount = typeof details.vote_count === 'number' && !isNaN(details.vote_count)
    ? details.vote_count.toLocaleString()
    : '0';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Back Button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="mb-6"
      >
        <Link
          to="/"
          className="inline-flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200 text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Home</span>
        </Link>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Poster and Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-1"
        >
          {/* Poster */}
          <div className="relative group">
            <img
              src={getImageUrl(details.poster_path, 'w500')}
              alt={title}
              className="w-full max-w-sm mx-auto rounded-xl shadow-2xl"
            />
            
            {/* Play Button Overlay */}
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl flex items-center justify-center">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleWatchTrailer}
                disabled={!hasTrailer}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium shadow-lg transition-all duration-200 ${
                  hasTrailer 
                    ? 'bg-white/90 backdrop-blur-sm text-gray-900 hover:bg-white'
                    : 'bg-gray-500/70 text-gray-300 cursor-not-allowed'
                }`}
              >
                <Play className="h-5 w-5" />
                <span>{hasTrailer ? 'Watch Trailer' : 'No Trailer'}</span>
              </motion.button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 space-y-3">
            {hasTrailer && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleWatchTrailer}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-all duration-200 shadow-lg"
              >
                <Play className="h-5 w-5" />
                <span>Watch Trailer</span>
              </motion.button>
            )}
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleFavoriteToggle}
              className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                isFavorite(details.id, type!)
                  ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg'
                  : 'bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 text-gray-700 dark:text-gray-300'
              }`}
            >
              <Heart className={`h-5 w-5 ${isFavorite(details.id, type!) ? 'fill-current' : ''}`} />
              <span>{isFavorite(details.id, type!) ? 'Remove from Favorites' : 'Add to Favorites'}</span>
            </motion.button>
          </div>

          {/* Quick Stats */}
          <div className="mt-6 bg-white dark:bg-dark-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-dark-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              <Award className="h-4 w-4 mr-2 text-primary-500" />
              Quick Stats
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Rating</span>
                <span className="font-medium text-gray-900 dark:text-white">{voteAverage}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Votes</span>
                <span className="font-medium text-gray-900 dark:text-white">{voteCount}</span>
              </div>
              {releaseDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Year</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {new Date(releaseDate).getFullYear()}
                  </span>
                </div>
              )}
              {runtime && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Runtime</span>
                  <span className="font-medium text-gray-900 dark:text-white">{runtime} min</span>
                </div>
              )}
              {status && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Status</span>
                  <span className="font-medium text-gray-900 dark:text-white">{status}</span>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Right Column - Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 space-y-6"
        >
          {/* Header */}
          <div className="space-y-4">
            {/* Type Badge */}
            <div className="flex items-center space-x-2">
              {type === 'movie' ? (
                <Film className="h-5 w-5 text-primary-500" />
              ) : (
                <Tv className="h-5 w-5 text-primary-500" />
              )}
              <span className="text-sm font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-3 py-1 rounded-full">
                {type === 'movie' ? 'Movie' : 'TV Series'}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white leading-tight">
              {title}
            </h1>

            {/* Tagline */}
            {tagline && (
              <p className="text-lg text-gray-600 dark:text-gray-400 italic">"{tagline}"</p>
            )}

            {/* Rating */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 bg-yellow-100 dark:bg-yellow-900/30 px-3 py-1 rounded-full">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="font-semibold text-gray-900 dark:text-white">{voteAverage}</span>
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                ({voteCount} votes)
              </span>
            </div>

            {/* Genres */}
            {genres && genres.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {genres.map((genre) => (
                  <span
                    key={genre.id}
                    className="px-3 py-1 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded-full text-sm font-medium"
                  >
                    {genre.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Overview */}
          {overview && (
            <div className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Overview</h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{overview}</p>
            </div>
          )}

          {/* Cast */}
          {credits && credits.cast.length > 0 && (
            <div className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Users className="h-5 w-5 mr-2 text-primary-500" />
                Cast
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {credits.cast.slice(0, 8).map((person) => (
                  <div key={person.id} className="text-center group">
                    <div className="relative mb-3">
                      <img
                        src={getImageUrl(person.profile_path, 'w185')}
                        alt={person.name}
                        className="w-16 h-16 rounded-full object-cover mx-auto shadow-md group-hover:shadow-lg transition-shadow duration-200"
                      />
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{person.name}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{person.character}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Trailer Modal */}
      <TrailerModal
        isOpen={isTrailerOpen}
        onClose={() => setIsTrailerOpen(false)}
        video={selectedTrailer}
      />
    </div>
  );
};

export default Details; 